import { WALLET_STATUS_LABELS_LAO } from "@/modules/wallet/domain/types";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

type WalletAccountsSearch = {
  status?: string;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function WalletAccountsFilter() {
  const nav = useNavigate({ from: "/app/wallet/accounts" });
  const search = useSearch({
    from: "/app/wallet/accounts",
  }) as WalletAccountsSearch;

  const hasFilter = !!search.status || !!search.search;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<WalletAccountsSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sortField: search.sortField,
        sortOrder: search.sortOrder,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Tabs
        className="w-full"
        value={search.status ?? FILTER_ALL}
        onValueChange={(v) => update({ status: toParam(v ?? "") })}
      >
        <TabsList className="h-9 flex-wrap gap-1 bg-muted/50">
          <TabsTrigger value={FILTER_ALL} className="text-xs">
            ທຸກສະຖານະ
          </TabsTrigger>
          <TabsTrigger value="ACTIVE" className="text-xs">
            {WALLET_STATUS_LABELS_LAO.ACTIVE}
          </TabsTrigger>
          <TabsTrigger value="SUSPENDED" className="text-xs">
            {WALLET_STATUS_LABELS_LAO.SUSPENDED}
          </TabsTrigger>
          <TabsTrigger value="CLOSED" className="text-xs">
            {WALLET_STATUS_LABELS_LAO.CLOSED}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Input
        type="search"
        placeholder="ຄົ້ນຫາລູກຄ້າ (ຊື່ / ເບີ)"
        className="w-56"
        defaultValue={searchInputValue}
        onChange={(e) => {
          debouncedSearch(e.target.value);
        }}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
